/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { clsx } from 'clsx';

interface AvatarProps {
  name?: string;
  src?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  status?: 'online' | 'offline' | 'away';
  fallbackNumber?: number;
  /** When true, pick a deterministic accent color based on `name` instead of the default primary gradient. */
  colored?: boolean;
}

const sizes = {
  sm: 'w-7 h-7 text-[10px]',
  md: 'w-9 h-9 text-xs',
  lg: 'w-11 h-11 text-sm',
};

const statusColors = {
  online: 'bg-status-success-fg',
  offline: 'bg-slate-300',
  away: 'bg-status-warning-fg',
};

// Tessarix-tinted accent palette for per-user avatar colors (matches mockup mix
// of blue/purple/pink/teal/etc.). Solid backgrounds, white foreground.
const palette = [
  'bg-primary',
  'bg-[#a855f7]',
  'bg-[#ec4899]',
  'bg-[#14b8a6]',
  'bg-[#f97316]',
  'bg-[#0ea5e9]',
  'bg-[#22c55e]',
];

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function getInitials(name?: string, fallbackNumber?: number): string {
  if (name) {
    return name
      .split(' ')
      .map((n) => n[0])
      .filter(Boolean)
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return fallbackNumber != null ? String(fallbackNumber) : '?';
}

function getColorClass(colored: boolean | undefined, name: string | undefined): string {
  if (colored && name) return palette[hashString(name) % palette.length];
  return 'bg-gradient-to-br from-primary-500 to-primary-700';
}

export default function Avatar({
  name,
  src,
  size = 'md',
  className,
  status,
  fallbackNumber,
  colored,
}: AvatarProps) {
  const initials = getInitials(name, fallbackNumber);
  const colorClass = getColorClass(colored, name);

  return (
    <div className={clsx('relative inline-flex shrink-0', className)}>
      {src ? (
        <img
          src={src}
          alt={name ?? ''}
          className={clsx('rounded-full object-cover', sizes[size])}
        />
      ) : (
        <div
          className={clsx(
            'rounded-full flex items-center justify-center font-bold text-white',
            colorClass,
            sizes[size]
          )}
        >
          {initials}
        </div>
      )}
      {status && (
        <span
          className={clsx(
            'absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white',
            statusColors[status]
          )}
        />
      )}
    </div>
  );
}
